import { useEffect } from "react";
import { createPortal } from "react-dom";
import { btnPrimary } from "./controlPlaneUi";

interface Props {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}

/** Minimal centered modal dialog: overlay-click, ✕, OK, and Escape all dismiss. Rendered via a
 *  portal to <body> so the host page's frequent re-renders/layout can't make it flicker. */
const Modal: React.FC<Props> = ({ title, onClose, children }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="w-full max-w-md border border-[#262420] bg-[#1a1917] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#262420] px-4 py-2.5">
          <h2 className="text-sm font-semibold text-[#E5E0D5]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[#5A5448] hover:text-[#E5E0D5]"
          >
            ✕
          </button>
        </div>
        <div className="px-4 py-4 text-sm text-[#9D9585]">{children}</div>
        <div className="flex justify-end border-t border-[#262420] px-4 py-3">
          <button type="button" className={btnPrimary} onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
